import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { AlertCircle, Download, FolderKanban, Loader2, Menu, Pin, Search, X } from "lucide-react";

import { NotificationInbox } from "@/components/notification-inbox";
import { SettingsMenu } from "@/components/settings-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAccessibleProjects } from "@/hooks/use-accessible-projects";
import type { Agent } from "@/hooks/use-agents";
import type { PushSubscriptionAPI } from "@/hooks/use-push-subscription";
import { useTenantUsers } from "@/hooks/use-tenant-users";
import type { UseUserPreferencesReturn } from "@/hooks/use-user-preferences";
import type { AccessibleProjectSummary } from "@/lib/project-types";
import { downloadCsv, downloadServerCsv } from "@/lib/csv-export";
import type { AccessTokenGetter } from "@/lib/utils";

interface ProjectsListPageProps {
  getToken: AccessTokenGetter;
  isMobile: boolean;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  isAdminUser: boolean;
  canManageTenantAgents?: boolean;
  user: { firstName?: string | null; email?: string | null };
  pushApi: PushSubscriptionAPI;
  preferencesApi: UseUserPreferencesReturn;
}

interface ProjectsListHeaderProps {
  search: string;
  onSearchChange: (value: string) => void;
  isMobile: boolean;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  isAdminUser: boolean;
  canManageTenantAgents?: boolean;
  user: { firstName?: string | null; email?: string | null };
  pushApi: PushSubscriptionAPI;
  preferencesApi: UseUserPreferencesReturn;
}

function projectHref(projectId: string): string {
  return `/project/${encodeURIComponent(projectId)}`;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatOwner(project: AccessibleProjectSummary, ownerNames: Map<string, string>): string {
  return ownerNames.get(project.created_by) ?? project.created_by;
}

function ProjectsListHeader({
  search,
  onSearchChange,
  isMobile,
  isMenuOpen,
  onMenuToggle,
  agents,
  agentsLoading,
  agentsError,
  isAdminUser,
  canManageTenantAgents,
  user,
  pushApi,
  preferencesApi,
}: ProjectsListHeaderProps) {
  const { t } = useTranslation();

  return (
    <header className="flex min-h-[53px] min-w-0 shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2.5">
      {isMobile && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          onClick={onMenuToggle}
          aria-label={isMenuOpen ? t("header.closeMenu") : t("header.openMenu")}
        >
          {isMenuOpen ? <X /> : <Menu />}
        </Button>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <FolderKanban className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <h1 className="min-w-0 truncate text-sm font-semibold">{t("projects:catalog.title")}</h1>
      </div>
      <label className="relative order-3 w-full sm:order-none sm:w-72">
        <span className="sr-only">{t("projects:catalog.search")}</span>
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t("projects:catalog.search")}
          className="pl-8"
        />
      </label>
      <Button asChild size="sm" className="shrink-0">
        <Link href="/project/new">{t("projects:sectionProjectsNew")}</Link>
      </Button>
      {isMobile && <>
        <NotificationInbox />
        <SettingsMenu
          className="shrink-0"
          user={user}
          isAdminUser={isAdminUser}
          canManageTenantAgents={canManageTenantAgents}
          pushApi={pushApi}
          preferencesApi={preferencesApi}
          agents={agents}
          agentsLoading={agentsLoading}
          agentsError={agentsError}
        />
      </>}
    </header>
  );
}

interface ProjectTableRowProps {
  project: AccessibleProjectSummary;
  owner: string;
  onOpen: () => void;
}

function ProjectVisibilityBadge({
  visibility,
}: {
  visibility: AccessibleProjectSummary["visibility"];
}) {
  const isShared = visibility === "Shared";
  return (
    <Badge
      variant={isShared ? "default" : "secondary"}
      className={`whitespace-nowrap ${
        isShared
          ? "bg-primary/10 text-primary shadow-none hover:bg-primary/20"
          : "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80"
      }`}
    >
      {visibility}
    </Badge>
  );
}

function ProjectNameLink({ project }: { project: AccessibleProjectSummary }) {
  const { t } = useTranslation();

  return (
    <Link
      href={projectHref(project.id)}
      className="inline-flex min-w-0 max-w-full items-start gap-1.5 rounded-sm font-medium text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      onClick={(event) => event.stopPropagation()}
    >
      {project.is_pinned && (
        <span className="mt-1 shrink-0 text-muted-foreground/60" title={t("projects:catalog.pinned")}>
          <Pin className="size-3.5 stroke-[1.5] fill-none" aria-hidden="true" />
          <span className="sr-only">{t("projects:catalog.pinned")}</span>
        </span>
      )}
      <span className="min-w-0 break-words">{project.name}</span>
    </Link>
  );
}

function ProjectTableRow({ project, owner, onOpen }: ProjectTableRowProps) {
  const { t } = useTranslation();
  const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <TableRow
      tabIndex={0}
      role="link"
      aria-label={t("projects:catalog.openProject", { name: project.name })}
      className="cursor-pointer border-b align-top transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring last:border-0"
      onClick={onOpen}
      onKeyDown={onKeyDown}
    >
      <TableCell className="min-w-[220px] w-1/4 py-3 pl-4 pr-3">
        <ProjectNameLink project={project} />
      </TableCell>
      <TableCell className="w-[19rem] max-w-[19rem] py-3 pr-3 text-sm text-muted-foreground">
        <p className="line-clamp-2 break-words">{project.description_preview || "—"}</p>
      </TableCell>
      <TableCell className="max-w-44 break-words py-3 pr-3 text-sm" title={owner}>{owner}</TableCell>
      <TableCell className="whitespace-nowrap py-3 pr-3 text-sm text-muted-foreground">
        {formatDateTime(project.last_activity_at)}
      </TableCell>
      <TableCell className="py-3 pr-3 text-center tabular-nums">{project.open_task_count}</TableCell>
      <TableCell className="py-3 pr-3 text-center tabular-nums">{project.session_count}</TableCell>
      <TableCell className="whitespace-nowrap py-3 pr-3">
        <ProjectVisibilityBadge visibility={project.visibility} />
      </TableCell>
      <TableCell className="whitespace-nowrap py-3 pr-4 text-sm text-muted-foreground">
        {formatDateTime(project.created_at)}
      </TableCell>
    </TableRow>
  );
}

type ProjectCardProps = ProjectTableRowProps;

function ProjectCard({ project, owner, onOpen }: ProjectCardProps) {
  const { t } = useTranslation();
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <Card
      asChild
      className="cursor-pointer p-4 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <article
        tabIndex={0}
        role="link"
        aria-label={t("projects:catalog.openProject", { name: project.name })}
        onClick={onOpen}
        onKeyDown={onKeyDown}
      >
        <ProjectNameLink project={project} />
      <p className="mt-2 line-clamp-2 break-words text-sm text-muted-foreground">
        {project.description_preview || "—"}
      </p>
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">{t("projects:catalog.owner")}</dt>
          <dd className="mt-0.5 break-words" title={owner}>{owner}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t("projects:catalog.access")}</dt>
          <dd className="mt-1">
            <ProjectVisibilityBadge visibility={project.visibility} />
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t("projects:catalog.lastActivity")}</dt>
          <dd className="mt-0.5">{formatDateTime(project.last_activity_at)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t("projects:catalog.created")}</dt>
          <dd className="mt-0.5">{formatDateTime(project.created_at)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t("projects:catalog.openTasks")}</dt>
          <dd className="mt-0.5 tabular-nums">{project.open_task_count}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t("projects:catalog.sessions")}</dt>
          <dd className="mt-0.5 tabular-nums">{project.session_count}</dd>
        </div>
      </dl>
      </article>
    </Card>
  );
}

interface ProjectsTableProps {
  projects: AccessibleProjectSummary[];
  ownerNames: Map<string, string>;
}

function ProjectsTable({ projects, ownerNames }: ProjectsTableProps) {
  const [, navigate] = useLocation();
  const { t } = useTranslation();

  return (
    <>
      <div className="hidden overflow-x-auto rounded-lg border md:block">
        <Table className="min-w-[1080px] border-collapse text-left">
          <TableHeader>
            <TableRow className="bg-muted/30 text-xs text-muted-foreground hover:bg-muted/30">
              <TableHead className="h-auto whitespace-nowrap py-2.5 pl-4 pr-3 font-medium">{t("projects:catalog.project")}</TableHead>
              <TableHead className="h-auto w-[19rem] max-w-[19rem] whitespace-nowrap py-2.5 pr-3 font-medium">{t("projects:catalog.description")}</TableHead>
              <TableHead className="h-auto whitespace-nowrap py-2.5 pr-3 font-medium">{t("projects:catalog.owner")}</TableHead>
              <TableHead className="h-auto whitespace-nowrap py-2.5 pr-3 font-medium">{t("projects:catalog.lastActivity")}</TableHead>
              <TableHead className="h-auto whitespace-nowrap py-2.5 pr-3 text-center font-medium">{t("projects:catalog.openTasks")}</TableHead>
              <TableHead className="h-auto whitespace-nowrap py-2.5 pr-3 text-center font-medium">{t("projects:catalog.sessions")}</TableHead>
              <TableHead className="h-auto whitespace-nowrap py-2.5 pr-3 font-medium">{t("projects:catalog.access")}</TableHead>
              <TableHead className="h-auto whitespace-nowrap py-2.5 pr-4 font-medium">{t("projects:catalog.created")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project) => (
              <ProjectTableRow
                key={project.id}
                project={project}
                owner={formatOwner(project, ownerNames)}
                onOpen={() => navigate(projectHref(project.id))}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="space-y-3 md:hidden">
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            owner={formatOwner(project, ownerNames)}
            onOpen={() => navigate(projectHref(project.id))}
          />
        ))}
      </div>
    </>
  );
}

function ProjectsListEmptyState({ search }: { search: string }) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-64 flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center text-muted-foreground">
      <FolderKanban className="size-8" aria-hidden="true" />
      <p className="text-sm font-medium">
        {search ? t("projects:catalog.noMatching") : t("projects:catalog.empty")}
      </p>
      <p className="text-sm">{t("projects:catalog.emptyHint")}</p>
    </div>
  );
}

function ProjectsListErrorState({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-64 flex-col items-center justify-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center text-destructive">
      <AlertCircle className="size-7" aria-hidden="true" />
      <p className="text-sm font-medium">{t("projects:catalog.loadFailed")}</p>
      <p className="max-w-lg text-sm text-muted-foreground">{error}</p>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        {t("common:actions.retry")}
      </Button>
    </div>
  );
}

export function ProjectsListPage({
  getToken,
  isMobile,
  isMenuOpen,
  onMenuToggle,
  agents,
  agentsLoading,
  agentsError,
  isAdminUser,
  canManageTenantAgents,
  user,
  pushApi,
  preferencesApi,
}: ProjectsListPageProps) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const [exportNotice, setExportNotice] = useState<"fallback" | "failed" | null>(null);
  const {
    projects,
    hasMore,
    isLoading,
    error,
    setSearchQuery,
    refresh,
    loadMore,
  } = useAccessibleProjects(getToken);
  const { tenantUsers } = useTenantUsers(getToken);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearchQuery(searchInput), 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput, setSearchQuery]);

  const ownerNames = useMemo(
    () => new Map(
      tenantUsers.map((user) => [
        user.id,
        user.display_name || user.email || user.id,
      ]),
    ),
    [tenantUsers],
  );

  const showInitialLoading = isLoading && projects.length === 0 && !error;
  const showErrorState = Boolean(error && projects.length === 0);

  const handleExport = async () => {
    setExportNotice(null);
    const params = new URLSearchParams();
    if (searchInput.trim()) params.set("q", searchInput.trim());
    try {
      if (await downloadServerCsv(`/api/projects/accessible/export.csv${params.size ? `?${params}` : ""}`, getToken)) return;
    } catch {
      setExportNotice("failed");
      return;
    }
    setExportNotice("fallback");
    downloadCsv(t("projects:catalog.title"), [
      t("projects:catalog.project"), t("projects:catalog.description"),
      t("projects:catalog.owner"), t("projects:catalog.lastActivity"),
      t("projects:catalog.openTasks"), t("projects:catalog.sessions"),
      t("projects:catalog.access"), t("projects:catalog.created"),
    ], projects.map((project) => [
      project.name, project.description_preview ?? "", formatOwner(project, ownerNames),
      formatDateTime(project.last_activity_at), project.open_task_count,
      project.session_count, project.visibility, formatDateTime(project.created_at),
    ]));
  };

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-foreground">
      <ProjectsListHeader
        search={searchInput}
        onSearchChange={setSearchInput}
        isMobile={isMobile}
        isMenuOpen={isMenuOpen}
        onMenuToggle={onMenuToggle}
        agents={agents}
        agentsLoading={agentsLoading}
        agentsError={agentsError}
        isAdminUser={isAdminUser}
        canManageTenantAgents={canManageTenantAgents}
        user={user}
        pushApi={pushApi}
        preferencesApi={preferencesApi}
      />
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full max-w-[96rem]">
          {showInitialLoading ? (
            <div className="flex min-h-64 items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              {t("projects:catalog.loading")}
            </div>
          ) : showErrorState ? (
            <ProjectsListErrorState
              error={error ?? t("projects:catalog.loadFailed")}
              onRetry={() => { void refresh(); }}
            />
          ) : projects.length === 0 ? (
            <ProjectsListEmptyState search={searchInput.trim()} />
          ) : (
            <>
              {error && (
                <div className="mb-3 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  <AlertCircle className="size-4 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1">{error}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 shrink-0 px-2 text-xs"
                    onClick={() => { void refresh(); }}
                  >
                    {t("common:actions.retry")}
                  </Button>
                </div>
              )}
              <div className="mb-3 flex justify-end">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button type="button" size="icon-sm" variant="outline" onClick={() => { void handleExport(); }} aria-label={t("projects:catalog.exportCsvFull")}>
                        <Download />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("projects:catalog.exportCsvFull")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {exportNotice && <p className="mb-3 text-sm text-muted-foreground" role="status">{t(exportNotice === "fallback" ? "projects:catalog.exportFallbackLoaded" : "projects:catalog.exportFailed")}</p>}
              <ProjectsTable projects={projects} ownerNames={ownerNames} />
            </>
          )}
          {hasMore && !showErrorState && (
            <div className="mt-4 flex justify-center">
              <Button type="button" variant="outline" onClick={() => { void loadMore(); }} disabled={isLoading}>
                {isLoading && <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />}
                {t("projects:catalog.loadMore")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
